import { Controller, useForm } from 'react-hook-form'
import {
  ICreateCharacter,
  ICreateCharacterForm,
  IRace,
} from '../../../../../../api/types/character'
import { FormRow } from '../../../../../../components/FormRow/FormRow'
import { ErrorExplanation } from '../../../../../../components/ErrorExplanation/ErrorExplanation'
import { MenuItem, Paper, TextField, Theme, Typography } from '@mui/material'
import { SubmitButton } from '../../../../../../components/SubmitButton/SubmitButton'
import React from 'react'
import { useApiErrors } from '../../../../../../hooks/useApiErrors'
import makeStyles from '@mui/styles/makeStyles'
import createStyles from '@mui/styles/createStyles'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  mutationCreateCharacter,
  mutationPlayerCreateCharacter,
} from '../../../../../../api/mutations'
import { authQueryKey, queryRacesBase, racesBaseQueryKey } from '../../../../../../api/queries'
import { fromAxiosError } from '../../../../../../context/GlobalError'
import { ErrorHandler } from '../../../../../../components/ErrorHandler/ErrorHandler'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 8,
    },
    statInput: {
      marginTop: '4px',
      marginBottom: '4px',
    },
    columns: {
      display: 'flex',
      width: '100%',
    },
    statsCol: {
      marginRight: '24px',
      width: '120px',
      flexShrink: 0,
    },
    colHeader: {},
    colContent: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
      alignContent: 'flex-start',
    },
    skillInput: {
      marginRight: '4px',
      width: '120px',
    },
    skillsCol: {
      marginLeft: '4px',
      flexGrow: 1,
    },
  })
)

interface StatInputProps {
  name: string
  label: string
  register: any
  statMin: number
  statMax: number
  error: any
  defaultValue?: number
}

const StatInput: React.FC<StatInputProps> = ({
  name,
  label,
  register,
  statMax,
  statMin,
  error,
  defaultValue,
}) => {
  const classes = useStyles()
  return (
    <FormRow>
      <TextField
        className={classes.statInput}
        name={name}
        label={label}
        defaultValue={defaultValue || 15}
        size="small"
        type="number"
        fullWidth={true}
        inputProps={{
          min: statMin,
          max: statMax,
          ref: register({
            min: { value: statMin, message: `Minimum value ${statMin}` },
            max: { value: statMax, message: `Maximum value ${statMax}` },
          }),
        }}
        error={!!error}
        helperText={error && error.message}
      />
    </FormRow>
  )
}

export interface CreateCharacterProps {
  gmMode?: boolean
  afterCreate?: (id: number) => void
}

export const CreateCharacter: React.FC<CreateCharacterProps> = ({ gmMode, afterCreate }) => {
  const { apiErrors, setApiErrors, setApiErrorsFromAxios } = useApiErrors()
  const { isLoading, error, data: races } = useQuery<IRace[]>(racesBaseQueryKey(), queryRacesBase)
  const { register, handleSubmit, errors, control } = useForm<ICreateCharacterForm>()
  const classes = useStyles()
  const queryClient = useQueryClient()
  const { mutate: createCharacterMutation, isLoading: isMutating } = useMutation(
    (newChar: ICreateCharacter) =>
      gmMode ? mutationCreateCharacter(newChar) : mutationPlayerCreateCharacter(newChar),
    {
      onError: (err: any) => {
        setApiErrorsFromAxios(err, 'Nie udało się stworzyć postaci')
      },
      onSuccess: (data: any) => {
        setApiErrors([])
        queryClient.invalidateQueries(authQueryKey())
        afterCreate && afterCreate(data?.data?.id)
      },
    }
  )

  const onSubmit = (data: ICreateCharacterForm) => {
    createCharacterMutation({
      name: data.name,
      race_id: data.race_id,
      base_stat: {
        hp: 0,
        mana: 0,
        defence: 0,
        speed: 0,
        strength: data.strength,
        condition: data.condition,
        endurance: data.endurance,
        dexterity: data.dexterity,
        agility: data.agility,
        perception: data.perception,
        intelligence: data.intelligence,
        wisdom: data.wisdom,
        charisma: data.charisma,
      },
      skill_stat: {
        acrobatics: data.acrobatics,
        alchemy: data.alchemy,
        arcana: data.arcana,
        art: data.art,
        athletics: data.athletics,
        investigation: data.investigation,
        history: data.history,
        intuition: data.intuition,
        engineering: data.engineering,
        hiding: data.hiding,
        legerdemain: data.legerdemain,
        manipulation: data.manipulation,
        medicine: data.medicine,
        occultism: data.occultism,
        overawing: data.overawing,
        persuasion: data.persuasion,
        survival: data.survival,
        nature: data.nature,
        religion: data.religion,
        runecraft: data.runecraft,
        craft: data.craft,
      },
      weapon_profs: {
        arms: data.arms,
        small_blades: data.small_blades,
        swords: data.swords,
        big_swords: data.big_swords,
        blunts: data.blunts,
        poles: data.poles,
        bows: data.bows,
        crossbows: data.crossbows,
        throwing: data.throwing,
      },
    })
  }

  interface WeaponProfInputData {
    name: string
    stat: string
    error: any
  }

  const profs: WeaponProfInputData[] = [
    {
      name: 'Walka wręcz',
      stat: 'arms',
      error: errors.arms,
    },
    {
      name: 'Małe ostrza',
      stat: 'small_blades',
      error: errors.small_blades,
    },
    {
      name: 'Miecze',
      stat: 'swords',
      error: errors.swords,
    },
    {
      name: 'Duże miecze',
      stat: 'big_swords',
      error: errors.big_swords,
    },
    {
      name: 'Obuchy',
      stat: 'blunts',
      error: errors.blunts,
    },
    {
      name: 'Broń drzewcowa',
      stat: 'poles',
      error: errors.poles,
    },
    {
      name: 'Łuki',
      stat: 'bows',
      error: errors.bows,
    },
    {
      name: 'Kusze',
      stat: 'crossbows',
      error: errors.crossbows,
    },
    {
      name: 'Broń miotana',
      stat: 'throwing',
      error: errors.throwing,
    },
  ]

  interface StatFormInputData {
    stat: string
    label: string
    error: any
  }

  const statsFormInputData: StatFormInputData[] = [
    {
      stat: 'strength',
      label: 'Siła',
      error: errors.strength,
    },
    {
      stat: 'condition',
      label: 'Kondycja',
      error: errors.condition,
    },
    {
      stat: 'endurance',
      label: 'Wytrzymałość',
      error: errors.endurance,
    },
    {
      stat: 'dexterity',
      label: 'Zręczność',
      error: errors.dexterity,
    },
    {
      stat: 'agility',
      label: 'Zwinność',
      error: errors.agility,
    },
    {
      stat: 'perception',
      label: 'Percepcja',
      error: errors.perception,
    },
    {
      stat: 'intelligence',
      label: 'Inteligencja',
      error: errors.intelligence,
    },
    {
      stat: 'wisdom',
      label: 'Mądrość',
      error: errors.wisdom,
    },
    {
      stat: 'charisma',
      label: 'Charyzma',
      error: errors.charisma,
    },
  ]

  interface SkillFormInputData {
    stat: string
    label: string
    error: any
  }

  const skillsFormInputData: SkillFormInputData[] = [
    {
      stat: 'acrobatics',
      label: 'Akrobatyka',
      error: errors.acrobatics,
    },
    {
      stat: 'alchemy',
      label: 'Alchemia',
      error: errors.alchemy,
    },
    {
      stat: 'arcana',
      label: 'Arkana',
      error: errors.arcana,
    },
    {
      stat: 'art',
      label: 'Artyzm',
      error: errors.art,
    },
    {
      stat: 'athletics',
      label: 'Atletyka',
      error: errors.athletics,
    },
    {
      stat: 'investigation',
      label: 'Dochodzenie',
      error: errors.investigation,
    },
    {
      stat: 'history',
      label: 'Historia',
      error: errors.history,
    },
    {
      stat: 'intuition',
      label: 'Intuicja',
      error: errors.intuition,
    },
    {
      stat: 'engineering',
      label: 'Inżynieria',
      error: errors.engineering,
    },
    {
      stat: 'hiding',
      label: 'Krycie się',
      error: errors.hiding,
    },
    {
      stat: 'legerdemain',
      label: 'Kuglarstwo',
      error: errors.legerdemain,
    },
    {
      stat: 'manipulation',
      label: 'Manipulacja',
      error: errors.manipulation,
    },
    {
      stat: 'medicine',
      label: 'Medycyna',
      error: errors.medicine,
    },
    {
      stat: 'occultism',
      label: 'Okultyzm',
      error: errors.occultism,
    },
    {
      stat: 'overawing',
      label: 'Onieśmielenie',
      error: errors.overawing,
    },
    {
      stat: 'persuasion',
      label: 'Perswazja',
      error: errors.persuasion,
    },
    {
      stat: 'survival',
      label: 'Przetrwanie',
      error: errors.survival,
    },
    {
      stat: 'nature',
      label: 'Przyroda',
      error: errors.nature,
    },
    {
      stat: 'religion',
      label: 'Religia',
      error: errors.religion,
    },
    {
      stat: 'runecraft',
      label: 'Runoznastwo',
      error: errors.runecraft,
    },
    {
      stat: 'craft',
      label: 'Rzemiosło',
      error: errors.craft,
    },
  ]

  const statMin = gmMode ? 1 : 10
  const statMax = gmMode ? 40 : 20
  const nameValidation = gmMode ? { required: 'Wymagane' } : {}

  return (
    <Paper className={classes.root}>
      <ErrorHandler loading={isLoading} error={fromAxiosError(error)}>
        {races && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormRow>
              <ErrorExplanation errors={apiErrors} />
            </FormRow>
            {gmMode && (
              <FormRow>
                <TextField
                  className={classes.statInput}
                  name="name"
                  label="Name"
                  fullWidth={true}
                  inputProps={{
                    ref: register(nameValidation),
                  }}
                />
              </FormRow>
            )}
            <FormRow>
              <Controller
                control={control}
                name="race_id"
                defaultValue={1}
                render={({ onChange, value, name, ref }) => (
                  <TextField
                    className={classes.statInput}
                    select
                    label="Race"
                    name="race_id"
                    fullWidth={true}
                    inputRef={ref}
                    onChange={val => onChange(val)}
                    defaultValue={1}
                  >
                    {races.map(race => (
                      <MenuItem key={race.id} value={race.id}>
                        {race.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </FormRow>

            <div className={classes.columns}>
              <div className={classes.statsCol}>
                <Typography variant="button" display="block" gutterBottom>
                  Cechy
                </Typography>
                {statsFormInputData.map(({ stat, label, error }) => (
                  <StatInput
                    name={stat}
                    label={label}
                    register={register}
                    statMin={statMin}
                    statMax={statMax}
                    error={error}
                    defaultValue={15}
                  />
                ))}
              </div>

              <div className={classes.skillsCol}>
                <div className={classes.colHeader}>
                  <Typography variant="button" display="block" gutterBottom>
                    Umiejętności
                  </Typography>
                </div>
                <div className={classes.colContent}>
                  {skillsFormInputData.map(({ stat, label, error }) => (
                    <div className={classes.skillInput}>
                      <StatInput
                        name={stat}
                        label={label}
                        register={register}
                        statMin={0}
                        statMax={20}
                        error={error}
                        defaultValue={10}
                      />
                    </div>
                  ))}
                </div>
                <div className={classes.colHeader}>
                  <Typography variant="button" display="block" gutterBottom>
                    Biegłości
                  </Typography>
                </div>
                <div className={classes.colContent}>
                  {profs.map(({ stat, name, error }) => (
                    <div className={classes.skillInput}>
                      <StatInput
                        name={stat}
                        label={name}
                        register={register}
                        statMin={1}
                        statMax={6}
                        error={error}
                        defaultValue={1}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <SubmitButton disabled={isMutating}>Stwórz</SubmitButton>
          </form>
        )}
      </ErrorHandler>
    </Paper>
  )
}
